import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { translations } from '../../../../locales/translations';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControl, FormControlLabel, InputBase, MenuItem, Select, TextField } from '@mui/material';
import { Button, InputLabel } from '@material-ui/core';
import { StatusFiltering } from './StatusFiltering';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { commonStyle } from '../../../../styles/theme/themes';
import { initialWorklistFilter, worklistFilter } from '../slice/types';
import { useWorkListDataSlice, fetchWorkList } from '../slice';
import { useDispatch, useSelector } from 'react-redux';
import { biStatus, biType } from '../../../../locales/types';
import SearchIcon from '@mui/icons-material/Search';
import { apiList } from '../../../../locales/apiList';
import { UseAxios } from '../../../../utils/UseAxios';
import { selectAccessRight, selectWorkListLoading } from '../slice/selectors';
import { FromToDatePicker } from './FromToDatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

const CustomizedInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderBottom: '1px solid #999',
    borderRadius: '0',
  },
}));

export function WorkListSearch(props) {
  const { t } = useTranslation();
  const { initialLoad, setTableLoading } = props;
  const { actions } = useWorkListDataSlice();
  const dispatch = useDispatch();
  const accessright = useSelector(selectAccessRight);
  const [searchFilter, setSearchFilter] = useState<worklistFilter>(initialWorklistFilter);
  const [allPending, setAllPending] = useState<boolean>(accessright.edit);
  const [selctedSection, setSelectedSection] = useState<biType | ''>('');
  const [selectedStatus, setSelectedStatus] = useState<biStatus | ''>('');
  const [remarkOnly, setRemarkOnly] = useState<boolean>(false);
  const loading = useSelector(selectWorkListLoading);
  const [selectedBISubmittedDateFrom, setSelectedBISubmittedDateFrom] = useState<string|null>(null);
  const [selectedBISubmittedDateTo, setSelectedBISubmittedDateTo] = useState<string|null>(null);

  const isInvalidDate = selectedBISubmittedDateFrom === 'Invalid Date' || selectedBISubmittedDateTo === 'Invalid Date';
  const isInvalidDateRange = !!selectedBISubmittedDateFrom && !!selectedBISubmittedDateTo && selectedBISubmittedDateFrom > selectedBISubmittedDateTo;
  const isInvalidDateTo = !!selectedBISubmittedDateTo && selectedBISubmittedDateTo > new Date().toISOString();
  const canNotSearch = isInvalidDate || isInvalidDateRange || isInvalidDateTo;

  const handleBISubmittedDateFromChange = (date: MaterialUiPickersDate | null, value?: string | null) => {
    if (date instanceof Date && !isNaN(date.getTime())) {
      setSelectedBISubmittedDateFrom(date.toISOString());
    } else if (date === null) {
      setSelectedBISubmittedDateFrom(null);
    } else {
      setSelectedBISubmittedDateFrom("Invalid Date");
    }
  };

  const handleBISubmittedDateToChange = (date: MaterialUiPickersDate | null, value?: string | null) => {
    if (date instanceof Date && !isNaN(date.getTime())) {
      setSelectedBISubmittedDateTo(date.toISOString());
    } else if (date === null) {
      setSelectedBISubmittedDateTo(null);
    } else {
      setSelectedBISubmittedDateTo("Invalid Date");
    }
  };

  React.useEffect(() => {
    setTableLoading(loading);
  }, [loading]);

  React.useEffect(() => {
    if (accessright.edit !== allPending) {
      setAllPending(accessright.edit);
    }
  }, [accessright]);

  const remarkOnlyChange = (e) => {
    setRemarkOnly(e.target.checked);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      submitOnClick();
    }
  };

  const submitOnClick = async () => {
    if (selectedBISubmittedDateFrom === 'Invalid Date' || selectedBISubmittedDateTo === 'Invalid Date') {
      return;
    }

    if (selectedBISubmittedDateFrom && selectedBISubmittedDateTo && selectedBISubmittedDateFrom > selectedBISubmittedDateTo) {
      return;
    }
    if (selectedBISubmittedDateTo && selectedBISubmittedDateTo > new Date().toISOString()) {
      return;
    }
    const filter = {
      ...searchFilter,
      contractType: searchFilter.contractType.filter(v => v !== ''),
      remark: remarkOnly,
      allPending,
      selctedSection,
      selectedStatus,
      selectedBISubmittedDateFrom,
      selectedBISubmittedDateTo,
    };
    dispatch(fetchWorkList(filter));
  };

  return (
    <Wrapper>
      <div className='search-header'><ManageSearchIcon />{t(translations.biSearch)}</div>
      <div className='search-fields'>
        <div className='row'>
          <TextField
            className='search-input-field'
            label={t(translations.contractNo)} size='small'
            defaultValue={searchFilter.contractNumber} variant='standard'
            onKeyPress={handleKeyPress}
            onChange={(e) => {
              setSearchFilter({
                contractNumber: e.target.value,
                customerCode: searchFilter.customerCode,
                contractType: searchFilter.contractType,
              });
            }}
            inputProps={{
              style: { fontSize: 12 },
            }}
          />
          <TextField
            className='search-input-field'
            label={t(translations.contractDetail.contractInformation.customerCode)} size='small'
            defaultValue={searchFilter.customerCode} variant='standard'
            onKeyPress={handleKeyPress}
            disabled={searchFilter.contractNumber !== ''}
            onChange={(e) => {
              setSearchFilter({
                contractNumber: searchFilter.contractNumber,
                customerCode: e.target.value,
                contractType: searchFilter.contractType,
              });
            }}
            inputProps={{
              style: { fontSize: 12 },
            }}
          />
          <FormControl sx={{ m: 1, minWidth: 140 }}>
            <InputLabel className='input-label'>
              {t(translations.contractType)}
            </InputLabel>

            <Select
              // value={selectContractType}
              multiple
              value={searchFilter.contractType}
              disabled={searchFilter.contractNumber !== ''}
              onChange={(e) => {
                // unset case
                // check all ('') is selected or deselected in the change by comparing searchFilter.contractType and e.target.value
                // Check if 'All' ('') is being selected or deselected
                const prev = searchFilter.contractType || [];
                const next = Array.isArray(e.target.value) ? e.target.value : [e.target.value];

                // Determine if the 'All' option changed (selected or deselected)
                const optionAllChanged = prev.includes('') !== next.includes('');

                const allValues = ['', 'New', 'Renew', 'OpenContract'];
                console.log(prev);
                console.log(next);
                console.log(next.length > prev.length && next.length === allValues.length - 1);
                if (!optionAllChanged) {
                  setSearchFilter({
                    contractNumber: searchFilter.contractNumber,
                    customerCode: searchFilter.customerCode,
                    contractType: (next.length > prev.length && next.length === allValues.length - 1) ? allValues : next.filter(v => v !== ''),
                  });
                } else {
                  if (next.includes('')) {
                    setSearchFilter({
                      contractNumber: searchFilter.contractNumber,
                      customerCode: searchFilter.customerCode,
                      contractType: allValues,
                    });
                  } else {
                    setSearchFilter({
                      contractNumber: searchFilter.contractNumber,
                      customerCode: searchFilter.customerCode,
                      contractType: [],
                    });
                  }
                }
              }}
              input={<CustomizedInput />}
            >
              <MenuItem value={''}>All</MenuItem>
              <MenuItem value={'New'}>New</MenuItem>
              <MenuItem value={'Renew'}>Renew</MenuItem>
              <MenuItem value={'OpenContract'}>Open Contract</MenuItem>
            </Select>
          </FormControl>
          {accessright.edit &&
            <>
            <FormControlLabel className='checkbox'
                              disabled={searchFilter.contractNumber !== ''}
                              control={<Checkbox checked={remarkOnly} onChange={remarkOnlyChange} />}
                              label={<div className='form-control-label'>Remarks Only</div>}
            />
            <StatusFiltering allPending={allPending} setAllPending={setAllPending}
                             setSelectedSection={setSelectedSection}
                             setSelectedStatus={setSelectedStatus} disabled={searchFilter.contractNumber !== ''} />
            </>
          }

          {/*<FromToDatePicker*/}
          {/*  label={translations.contractDetail.amendment.contractStartDate}*/}
          {/*  fromValue={selectedContractStartDateFrom}*/}
          {/*  fromValueChange={handleContractStartDateFromChange}*/}
          {/*  toValue={selectedContractStartDateTo}*/}
          {/*  toValueChange={handleContractStartDateToChange}*/}
          {/*/>*/}
          <Button className={`search-btn ${!loading && 'not-loading'} ${canNotSearch ? 'disabled' : ''}`}
                  variant='contained' onClick={submitOnClick}
                  color={canNotSearch ? 'primary' : undefined}
                  disabled={loading || canNotSearch} startIcon={<SearchIcon />}>
            {t(translations.search)}
          </Button>
        </div>
        <div className='row'>
          <FromToDatePicker
              label={translations.contractDetail.contractInformation.biCreateDate}
              fromValue={selectedBISubmittedDateFrom}
              fromValueChange={handleBISubmittedDateFromChange}
              toValue={selectedBISubmittedDateTo}
              toValueChange={handleBISubmittedDateToChange}
              disabled={searchFilter.contractNumber !== ''}
            />
        </div>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  margin-bottom: 5px;
  border-radius: 5px;
  box-shadow: ${commonStyle.boxShadow};

  .search-header {
    display: flex;
    align-items: center;
    padding: 10px;
    border-radius: 5px;
    background-color: ${p => p.theme.primary};
    color: ${p => p.theme.background};
    font-size: 16px;
  }

  .search-fields {
    border-radius: 5px;
    padding: 10px;
    height: 85%;

    .row {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;

      .search-input-field {
        margin-top: 2px;
        margin-right: 7px;
      }

      .search-btn {
        width: 110px;
        font-size: 12px;
        padding: 5px;
        height: 30px;
        margin-left: auto;
        margin-right: 0;
      }

      .not-loading {
        color: ${p => p.theme.buttonFont};
        background: ${p => p.theme.buttonColor};
      }

      .disabled {
        color:#747272;
        background: #e0e0e0;
      }
    }
  }

  .checkbox {
    padding-left: 10px;
  }

  .form-control-label {
    font-size: 14px;
    padding-left: 5px;
  }

  .MuiInputLabel-root {
    font-size: 14px;
  }

  .MuiSelect-select {
    font-size: 14px;
  }

  .MuiFormControl-root {
    margin: 0;
  }

  .input-label {
    font-size: 12px;
    margin-bottom: 3px;
  }

  .input-select {
    min-width: 100px;
    margin-top: 0;
    margin-right: 10px;
  }

  .MuiNativeSelect-select {
    font-size: 14px;
  }
`;
